import axios from './axios.client.js';

const BASE_URL = '/api/v1/notifications';

const notificationApi = {
  // Get all notifications with optional filters
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await axios.get(`${BASE_URL}?${params.toString()}`);
    return response.data;
  },

  // Get notification by ID
  getById: async (id) => {
    const response = await axios.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  // Create new notification
  create: async (notificationData) => {
    const response = await axios.post(BASE_URL, notificationData);
    return response.data;
  },

  // Update notification
  update: async (id, notificationData) => {
    const response = await axios.put(`${BASE_URL}/${id}`, notificationData);
    return response.data;
  },

  // Delete notification
  delete: async (id) => {
    const response = await axios.delete(`${BASE_URL}/${id}`);
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (id) => {
    const response = await axios.patch(`${BASE_URL}/${id}/read`);
    return response.data;
  },

  // Mark notification as unread
  markAsUnread: async (id) => {
    const response = await axios.patch(`${BASE_URL}/${id}/unread`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await axios.patch(`${BASE_URL}/mark-all-read`);
    return response.data;
  },

  // Get notification statistics
  getStats: async () => {
    const response = await axios.get(`${BASE_URL}/stats`);
    return response.data;
  },

  // Bulk import notifications
  bulkImport: async (notifications) => {
    const response = await axios.post(`${BASE_URL}/bulk-import`, { notifications });
    return response.data;
  },

  // Bulk export notifications
  bulkExport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await axios.get(`${BASE_URL}/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get notification templates
  getTemplates: async () => {
    const response = await axios.get(`${BASE_URL}/templates`);
    return response.data;
  },

  // Create notification template
  createTemplate: async (templateData) => {
    const response = await axios.post(`${BASE_URL}/templates`, templateData);
    return response.data;
  },

  // Update notification template
  updateTemplate: async (id, templateData) => {
    const response = await axios.put(`${BASE_URL}/templates/${id}`, templateData);
    return response.data;
  },

  // Delete notification template
  deleteTemplate: async (id) => {
    const response = await axios.delete(`${BASE_URL}/templates/${id}`);
    return response.data;
  },

  // Send test notification
  sendTest: async (notificationData) => {
    const response = await axios.post(`${BASE_URL}/send-test`, notificationData);
    return response.data;
  },

  // Get notification settings
  getSettings: async () => {
    const response = await axios.get(`${BASE_URL}/settings`);
    return response.data;
  },

  // Update notification settings
  updateSettings: async (settings) => {
    const response = await axios.put(`${BASE_URL}/settings`, settings);
    return response.data;
  },

  // Get notification channels
  getChannels: async () => {
    const response = await axios.get(`${BASE_URL}/channels`);
    return response.data;
  },

  // Test notification channel
  testChannel: async (channel, testData) => {
    const response = await axios.post(`${BASE_URL}/channels/${channel}/test`, testData);
    return response.data;
  },

  // Get notification types
  getTypes: async () => {
    const response = await axios.get(`${BASE_URL}/types`);
    return response.data;
  },

  // Get notification priorities
  getPriorities: async () => {
    const response = await axios.get(`${BASE_URL}/priorities`);
    return response.data;
  },

  // Search notifications
  search: async (query, filters = {}) => {
    const params = new URLSearchParams();
    params.append('q', query);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await axios.get(`${BASE_URL}/search?${params.toString()}`);
    return response.data;
  },

  // Get notification timeline
  getTimeline: async (id) => {
    const response = await axios.get(`${BASE_URL}/${id}/timeline`);
    return response.data;
  },

  // Get notification analytics
  getAnalytics: async (period = '30d') => {
    const response = await axios.get(`${BASE_URL}/analytics?period=${period}`);
    return response.data;
  },

  // Subscribe to notification updates
  subscribe: async (subscriptionData) => {
    const response = await axios.post(`${BASE_URL}/subscribe`, subscriptionData);
    return response.data;
  },

  // Unsubscribe from notification updates
  unsubscribe: async (subscriptionId) => {
    const response = await axios.delete(`${BASE_URL}/subscribe/${subscriptionId}`);
    return response.data;
  },

  // Get user notification preferences
  getPreferences: async () => {
    const response = await axios.get(`${BASE_URL}/preferences`);
    return response.data;
  },

  // Update user notification preferences
  updatePreferences: async (preferences) => {
    const response = await axios.put(`${BASE_URL}/preferences`, preferences);
    return response.data;
  },

  // Get notification history
  getHistory: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await axios.get(`${BASE_URL}/history?${params.toString()}`);
    return response.data;
  },

  // Archive notification
  archive: async (id) => {
    const response = await axios.patch(`${BASE_URL}/${id}/archive`);
    return response.data;
  },

  // Restore archived notification
  restore: async (id) => {
    const response = await axios.patch(`${BASE_URL}/${id}/restore`);
    return response.data;
  },

  // Get archived notifications
  getArchived: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await axios.get(`${BASE_URL}/archived?${params.toString()}`);
    return response.data;
  },

  // Bulk operations
  bulkMarkAsRead: async (ids) => {
    const response = await axios.patch(`${BASE_URL}/bulk-read`, { ids });
    return response.data;
  },

  bulkMarkAsUnread: async (ids) => {
    const response = await axios.patch(`${BASE_URL}/bulk-unread`, { ids });
    return response.data;
  },

  bulkDelete: async (ids) => {
    const response = await axios.delete(`${BASE_URL}/bulk-delete`, { data: { ids } });
    return response.data;
  },

  bulkArchive: async (ids) => {
    const response = await axios.patch(`${BASE_URL}/bulk-archive`, { ids });
    return response.data;
  },

  bulkRestore: async (ids) => {
    const response = await axios.patch(`${BASE_URL}/bulk-restore`, { ids });
    return response.data;
  },
};

export { notificationApi };
export default notificationApi;
